/**
 * Favorites API — server-synced favorites per user.
 *
 * Stores favorites as a string[] in the KV store under key:
 *   __favorites:{username}
 *
 * Uses the dedicated POST /content/bulk-read endpoint for reading
 * specific keys, and content/bulk PUT for writing.
 *
 * Also maintains a localStorage mirror for instant reads so the UI
 * doesn't flicker while waiting for the server response.
 */

import { apiFetch } from "./apiClient";

const KV_PREFIX = "__favorites:";

function kvKey(username: string): string {
  return KV_PREFIX + username.toLowerCase();
}

function localKey(username: string): string {
  return `tpl_favorites_${username}`;
}

/** Read favorites from localStorage (instant, may be stale). */
export function readLocalFavorites(username: string): string[] {
  try {
    const raw = localStorage.getItem(localKey(username));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Write favorites to localStorage (instant mirror). */
export function writeLocalFavorites(username: string, ids: string[]): void {
  try {
    localStorage.setItem(localKey(username), JSON.stringify(ids));
  } catch { /* ignore */ }
}

/**
 * Load favorites from the server (KV store).
 * Falls back to localStorage on error.
 */
export async function loadServerFavorites(username: string): Promise<string[]> {
  try {
    // Read the specific favorites key via the bulk-read endpoint
    const data = await apiFetch("/content/bulk-read", {
      method: "POST",
      body: JSON.stringify({ keys: [kvKey(username)] }),
    });
    if (data?.values) {
      const value = data.values[kvKey(username)];
      if (Array.isArray(value)) {
        writeLocalFavorites(username, value);
        return value;
      }
    }
    // Key not found on server — return empty (or whatever localStorage has)
    return readLocalFavorites(username);
  } catch {
    // Server error — fall back to localStorage
    return readLocalFavorites(username);
  }
}

/**
 * Save favorites to the server + localStorage mirror.
 * Fire-and-forget: the caller doesn't need to await this.
 */
export async function saveServerFavorites(
  username: string,
  ids: string[],
): Promise<void> {
  // Immediately mirror to localStorage
  writeLocalFavorites(username, ids);

  // Persist to KV store via content/bulk
  try {
    await apiFetch("/content/bulk", {
      method: "PUT",
      body: JSON.stringify({
        overrides: { [kvKey(username)]: ids },
      }),
    });
  } catch (err) {
    console.warn("[Favorites] Failed to sync to server:", err);
    // localStorage is still updated — user won't lose data this session
  }
}
